import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { SEO } from "../components/SEO";
import { GoogleSignInButton, AuthDivider } from "../components/GoogleSignInButton";
import { API, LOGO_MARK } from "../lib/site";

export const MEMBER_TOKEN_KEY = "ggh_member_token";

const MemberRegister = () => {
    const [form, setForm] = useState({ name: "", email: "", password: "", country: "" });
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
        if (form.password.length < 8) er.password = "Password must be at least 8 characters.";
        setErrors(er);
        if (Object.keys(er).length) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/members/register`, form);
            localStorage.setItem(MEMBER_TOKEN_KEY, data.token);
            navigate("/member/dashboard?welcome=1");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setErrors({ form: typeof detail === "string" ? detail : "Could not create your account. Please try again." });
        } finally {
            setBusy(false);
        }
    };

    const labelCls = "mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70";
    const errCls = "mt-2 text-xs font-semibold text-[#E8A0A0]";

    return (
        <main>
            <SEO title="Create Member Account | Global Green Hotel" description="Create your free GGH member account and join the movement for sustainable hospitality." />
            <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5 py-32">
                <form
                    onSubmit={submit}
                    noValidate
                    data-testid="member-register-form"
                    className="w-full max-w-md rounded-2xl border border-white/10 bg-[#07211A] p-9"
                >
                    <img src={LOGO_MARK} alt="Global Green Hotel logo" className="w-36" />
                    <h1 className="font-display mt-7 text-3xl font-medium text-[#FDFBF7]">Create your account</h1>
                    <p className="mt-2 text-sm text-[#FDFBF7]/60">
                        A free account for individuals who want to follow and join the GGH movement.
                    </p>
                    <div className="mt-8">
                        <GoogleSignInButton testid="member-register-google" label="Sign up with Google" />
                    </div>
                    <AuthDivider />
                    <div className="mt-8">
                        <label htmlFor="mr-name" className={labelCls}>Full Name</label>
                        <input id="mr-name" data-testid="member-register-name" type="text" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} className={`input-field ${errors.name ? "input-error" : ""}`} placeholder="Your full name" />
                        {errors.name && <p className={errCls} role="alert">{errors.name}</p>}
                    </div>
                    <div className="mt-5">
                        <label htmlFor="mr-email" className={labelCls}>Email</label>
                        <input id="mr-email" data-testid="member-register-email" type="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} className={`input-field ${errors.email ? "input-error" : ""}`} placeholder="you@example.com" autoComplete="email" />
                        {errors.email && <p className={errCls} role="alert">{errors.email}</p>}
                    </div>
                    <div className="mt-5">
                        <label htmlFor="mr-password" className={labelCls}>Create Password</label>
                        <input id="mr-password" data-testid="member-register-password" type="password" value={form.password} onChange={set("password")} aria-invalid={!!errors.password} className={`input-field ${errors.password ? "input-error" : ""}`} placeholder="At least 8 characters" autoComplete="new-password" />
                        {errors.password && <p className={errCls} role="alert">{errors.password}</p>}
                    </div>
                    <div className="mt-5">
                        <label htmlFor="mr-country" className={labelCls}>
                            Country <span className="font-semibold normal-case tracking-normal text-[#FDFBF7]/40">(optional)</span>
                        </label>
                        <input id="mr-country" data-testid="member-register-country" type="text" value={form.country} onChange={set("country")} className="input-field" placeholder="Country" />
                    </div>
                    {errors.form && (
                        <p data-testid="member-register-error" role="alert" className="mt-4 text-sm font-semibold text-[#E8A0A0]">
                            {errors.form}
                        </p>
                    )}
                    <button type="submit" data-testid="member-register-submit" disabled={busy} className="btn-gold mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60">
                        {busy ? "Creating your account…" : "Create Account"}
                    </button>
                    <p className="mt-6 text-center text-xs text-[#FDFBF7]/50">
                        Already have an account?{" "}
                        <Link to="/login?tab=member" data-testid="member-register-login" className="font-bold text-[#D9B96A] hover:underline">
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
        </main>
    );
};

export default MemberRegister;
